/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef } from "react";
import { BEM, useToggle, useTranslate } from "@adapptio/lib-components";
import { IFieldValidationError } from "../FieldTypes";
import { FieldText } from "../FieldText";
import { FieldWrapper } from "../FieldWrapper";
import { ButtonBase, FC_ANCHOR, FC_DIRECTION, FC_SIZE, Icon } from "../../core";
import { FieldStatus } from "../FieldStatus";
import {
	FIELD_FLOATING_PANEL_SCHEMA,
	FIELD_FLOATING_PANEL_STYLE,
	FieldFloatingPanel
} from "../FieldFloatingPanel";
import { FieldLabel } from "../FieldLabel";

import "./FieldSecret.scss";

export interface IFieldSecretProps {
	secretKey: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	grow?: boolean;
	onChange?: (secretKey: string) => void;
}

export const FieldSecret: React.FC<IFieldSecretProps> = ({
	secretKey,
	readOnly,
	validationErrors,
	className,
	grow,
	onChange,
}) => {
	const t = useTranslate();
	const elRef = useRef();
	const [ configOpen, toggleConfig, openConfig, closeConfig ] = useToggle();

	const baseClass = BEM("edt-field-secret", null, {
		"config-open": configOpen
	});

	const configClass = BEM("edt-field--type-secret", "config");

	const handleOpenConfigClick = useCallback<React.MouseEventHandler<HTMLElement>>(
		(event) => {
			if (readOnly) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			openConfig();
		},
		[ openConfig, readOnly ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<FieldWrapper type="secret" className={className} grow={grow} onClick={handleOpenConfigClick}>
			<ButtonBase
				className={baseClass.toClassName()}
				onClick={toggleConfig}
				ref={elRef}
				disabled={readOnly}
			>
				<span className={baseClass.element("value").toClassName()}>
					{secretKey
						? t("editor", "Secret:") + " " + secretKey
						: t("editor", "Click to configure secret")}
				</span>
				<FieldStatus errors={validationErrors} />
				<div className={baseClass.element("icon-wrapper").toClassName()}>
					<Icon className={baseClass.element("icon").toClassName()} name="mdi/lock" />
				</div>
			</ButtonBase>
			<FieldFloatingPanel
				for={elRef}
				isOpened={configOpen}
				anchor={FC_ANCHOR.CENTER_BOTTOM}
				direction={FC_DIRECTION.HR}
				size={FC_SIZE.FIT_MIN}
				style={FIELD_FLOATING_PANEL_STYLE.STICKY}
				schema={FIELD_FLOATING_PANEL_SCHEMA.PRIMARY}
				onClose={closeConfig}
			>
				<div className={configClass.element("config-field").toClassName()}>
					<FieldLabel>{t("editor", "Secret key:")}</FieldLabel>
					<FieldText
						value={secretKey}
						onChange={onChange}
						autoFocus
					/>
				</div>
			</FieldFloatingPanel>
		</FieldWrapper>
	);
};
